import { useEffect, useState } from "react";
import api from "../../lib/api";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "../ui/card";
import { Badge } from "../ui/badge";
import { FileCheck2, ShieldCheck, ChevronRight } from "lucide-react";

/**
 * Dashboard portfolio-level suite health strip.
 * One row per active-stage lead — approved/total progress bar with segments
 * for reviewed / needs_customisation / generated.
 */
export default function SuiteHealthStrip() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/documents/leads/health")
      .then((r) => setRows(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const withDocs = rows.filter((r) => r.total > 0);
  if (loading) return null;
  const showEmpty = rows.length > 0 && withDocs.length === 0;

  return (
    <Card className="border-slate-200 shadow-sm" data-testid="suite-health-strip">
      <CardContent className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-1 flex items-center gap-1.5">
              <ShieldCheck className="w-3.5 h-3.5" />Audit-readiness portfolio
            </p>
            <h3 className="text-lg font-semibold text-[#0D1B2A]">Document suite health</h3>
            <p className="text-xs text-slate-500 mt-0.5">Active-stage clients with a generated document suite.</p>
          </div>
          {!showEmpty && <Badge variant="outline" className="text-xs">{withDocs.length} client{withDocs.length === 1 ? "" : "s"}</Badge>}
        </div>
        {showEmpty ? (
          <div className="p-6 border border-dashed border-slate-200 rounded text-center" data-testid="health-empty-state">
            <FileCheck2 className="w-8 h-8 mx-auto text-slate-300 mb-2" />
            <p className="text-sm text-slate-500">
              No document suites generated yet. Open a lead → Documents tab → <span className="font-medium text-[#0D1B2A]">Generate suite</span> to get started.
            </p>
          </div>
        ) : (
          <ul className="space-y-3">
            {withDocs.map((r) => (
              <li
                key={r.lead_id}
                className="cursor-pointer hover:bg-slate-50 -mx-2 px-2 py-2 rounded transition-colors"
                onClick={() => nav(`/crm?lead=${r.lead_id}`)}
                data-testid={`health-row-${r.lead_id}`}
              >
                <div className="flex items-center gap-3 mb-1.5">
                  <FileCheck2 className={`w-4 h-4 shrink-0 ${r.percent >= 100 ? "text-[#1A7F6E]" : r.percent >= 60 ? "text-amber-500" : "text-slate-400"}`} />
                  <p className="text-sm font-medium text-[#0D1B2A] flex-1 truncate">{r.org_name}</p>
                  <span className="text-xs font-semibold text-[#0D1B2A]" data-testid={`health-count-${r.lead_id}`}>
                    {r.approved}/{r.total}
                  </span>
                  <span className={`text-xs w-10 text-right font-semibold ${r.percent >= 100 ? "text-[#1A7F6E]" : r.percent >= 60 ? "text-amber-600" : "text-slate-500"}`}>{r.percent}%</span>
                  <ChevronRight className="w-4 h-4 text-slate-300" />
                </div>
                <ProgressBar row={r} />
                <div className="flex items-center gap-3 mt-1 text-[10px] text-slate-500">
                  {r.needs_customisation > 0 && <span className="flex items-center gap-1"><span className="w-2 h-2 bg-amber-400 rounded-sm inline-block" />{r.needs_customisation} need customisation</span>}
                  {r.reviewed > 0 && <span className="flex items-center gap-1"><span className="w-2 h-2 bg-sky-400 rounded-sm inline-block" />{r.reviewed} reviewed</span>}
                  {r.generated > 0 && <span className="flex items-center gap-1"><span className="w-2 h-2 bg-slate-300 rounded-sm inline-block" />{r.generated} draft</span>}
                  <span className="uppercase tracking-widest text-[9px] text-slate-400 ml-auto">{r.stage?.replace("_", " ")}</span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function ProgressBar({ row }) {
  const { total, approved, reviewed, needs_customisation: nc, generated } = row;
  const seg = (n) => (n / total) * 100;
  return (
    <div className="h-2 bg-slate-100 rounded overflow-hidden flex" title={`${approved} approved · ${reviewed} reviewed · ${nc} need customisation · ${generated} draft`}>
      <span style={{ width: `${seg(approved)}%` }} className="bg-[#1A7F6E] block" />
      <span style={{ width: `${seg(reviewed)}%` }} className="bg-sky-400 block" />
      <span style={{ width: `${seg(nc)}%` }} className="bg-amber-400 block" />
      <span style={{ width: `${seg(generated)}%` }} className="bg-slate-300 block" />
    </div>
  );
}
